import { useCallback, useEffect, useState } from "react";
import { titles, TitleItem } from "@/mocks/titles";

const FAVORITES_KEY = "midnight-cinema:favorites";
const HISTORY_KEY = "midnight-cinema:history";
const MAX_HISTORY = 6;

const readIds = (key: string): string[] => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === "string") : [];
  } catch {
    return [];
  }
};

const writeIds = (key: string, ids: string[]) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(ids));
  } catch {
    // ignore storage failures (private mode, quota)
  }
};

export const useFavorites = () => {
  const [favorites, setFavorites] = useState<string[]>([]);

  useEffect(() => {
    setFavorites(readIds(FAVORITES_KEY));
  }, []);

  const toggleFavorite = useCallback((id: string) => {
    setFavorites((prev) => {
      const next = prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id];
      writeIds(FAVORITES_KEY, next);
      return next;
    });
  }, []);

  const isFavorite = useCallback((id: string) => favorites.includes(id), [favorites]);

  return { favorites, toggleFavorite, isFavorite, count: favorites.length };
};

export const useRecentlyViewed = () => {
  const [ids, setIds] = useState<string[]>([]);

  useEffect(() => {
    setIds(readIds(HISTORY_KEY));
  }, []);

  const recordView = useCallback((id: string) => {
    setIds((prev) => {
      const next = [id, ...prev.filter((item) => item !== id)].slice(0, MAX_HISTORY);
      writeIds(HISTORY_KEY, next);
      return next;
    });
  }, []);

  const recent: TitleItem[] = ids
    .map((id) => titles.find((item) => item.id === id))
    .filter((item): item is TitleItem => Boolean(item));

  return { recent, recordView };
};